import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, ArrowLeft, ChevronDown, ChevronRight, BookOpen, 
  Building2, Users, Lock, Server, FileText, AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const [expandedClauses, setExpandedClauses] = useState({});
  const [expandedControls, setExpandedControls] = useState({});

  const toggleClause = (id) => {
    setExpandedClauses(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleControl = (id) => {
    setExpandedControls(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const clauses = [
    {
      id: 1,
      title: "Clause 1 — Scope",
      description: "Defines what ISO 27001 covers: requirements for establishing, implementing, maintaining, and continually improving an Information Security Management System (ISMS).",
      subclauses: []
    },
    {
      id: 2,
      title: "Clause 2 — Normative References",
      description: "Lists other standards that are required to apply ISO 27001 (primarily ISO/IEC 27000).",
      subclauses: []
    },
    {
      id: 3,
      title: "Clause 3 — Terms and Definitions",
      description: "Provides standard definitions so terms like \"risk,\" \"control,\" and \"information security\" are interpreted consistently.",
      subclauses: []
    },
    {
      id: 4,
      title: "Clause 4 — Context of the Organization",
      purpose: "Understand the business environment and scope of the ISMS.",
      subclauses: [
        "4.1 Understand internal and external issues affecting information security",
        "4.2 Identify interested parties (customers, regulators, suppliers) and their needs",
        "4.3 Define the ISMS scope (what's included/excluded)",
        "4.4 Establish, implement, and maintain the ISMS"
      ]
    },
    {
      id: 5,
      title: "Clause 5 — Leadership",
      purpose: "Ensure top management ownership and direction.",
      subclauses: [
        "5.1 Leadership commitment to information security",
        "5.2 Establish and maintain an information security policy",
        "5.3 Assign clear roles and responsibilities"
      ]
    },
    {
      id: 6,
      title: "Clause 6 — Planning",
      purpose: "Plan how to manage risks and set objectives.",
      subclauses: [
        "6.1 Identify, assess, and treat information security risks",
        "6.2 Define measurable information security objectives",
        "6.3 Plan changes to the ISMS in a controlled way"
      ]
    },
    {
      id: 7,
      title: "Clause 7 — Support",
      purpose: "Provide resources and competence to run the ISMS.",
      subclauses: [
        "7.1 Ensure adequate resources",
        "7.2 Ensure staff competence",
        "7.3 Maintain awareness of security responsibilities",
        "7.4 Manage internal and external communications",
        "7.5 Control documented information (policies, procedures, records)"
      ]
    },
    {
      id: 8,
      title: "Clause 8 — Operation",
      purpose: "Execute the security plans and controls.",
      subclauses: [
        "8.1 Operational planning and control",
        "8.2 Perform information security risk assessments",
        "8.3 Apply risk treatment plans"
      ]
    },
    {
      id: 9,
      title: "Clause 9 — Performance Evaluation",
      purpose: "Measure effectiveness of the ISMS.",
      subclauses: [
        "9.1 Monitor, measure, analyze, and evaluate performance",
        "9.2 Conduct internal audits",
        "9.3 Perform management reviews"
      ]
    },
    {
      id: 10,
      title: "Clause 10 — Improvement",
      purpose: "Fix problems and continually improve.",
      subclauses: [
        "10.1 Manage nonconformities and corrective actions",
        "10.2 Continually improve the ISMS"
      ]
    }
  ];

  const annexAControls = [
    {
      id: "A5",
      title: "A.5 — Organizational Controls",
      icon: Building2,
      color: "grey",
      controls: [
        { id: "A.5.1", title: "Information security policies", desc: "Define management direction and expectations" },
        { id: "A.5.2", title: "Roles and responsibilities", desc: "Assign accountability for security tasks" },
        { id: "A.5.3", title: "Segregation of duties", desc: "Prevent conflicts of interest and fraud" },
        { id: "A.5.4", title: "Management responsibilities", desc: "Ensure managers enforce security" },
        { id: "A.5.5", title: "Contact with authorities", desc: "Maintain relationships with regulators/law enforcement" },
        { id: "A.5.6", title: "Contact with interest groups", desc: "Stay informed on security trends" },
        { id: "A.5.7", title: "Threat intelligence", desc: "Collect and analyze threat information" },
        { id: "A.5.8", title: "Project management", desc: "Integrate security into projects" },
        { id: "A.5.9", title: "Asset inventory", desc: "Identify information and related assets" },
        { id: "A.5.10", title: "Acceptable use", desc: "Define how assets may be used" },
        { id: "A.5.11", title: "Return of assets", desc: "Ensure assets are returned when roles end" },
        { id: "A.5.12", title: "Information classification", desc: "Classify data based on sensitivity" },
        { id: "A.5.13", title: "Information labelling", desc: "Mark data according to classification" },
        { id: "A.5.14", title: "Information transfer", desc: "Protect data when shared" },
        { id: "A.5.15", title: "Access control", desc: "Control access to information and systems" },
        { id: "A.5.16", title: "Identity management", desc: "Manage user identities lifecycle" },
        { id: "A.5.17", title: "Authentication information", desc: "Protect passwords and credentials" },
        { id: "A.5.18", title: "Access rights", desc: "Grant and revoke access appropriately" },
        { id: "A.5.19", title: "Supplier security", desc: "Manage risks from suppliers" },
        { id: "A.5.20", title: "Supplier agreements", desc: "Include security requirements in contracts" },
        { id: "A.5.21", title: "ICT supply chain security", desc: "Secure technology supply chains" },
        { id: "A.5.22", title: "Supplier service monitoring", desc: "Review supplier security performance" },
        { id: "A.5.23", title: "Cloud services security", desc: "Secure use of cloud environments" },
        { id: "A.5.24", title: "Incident preparation", desc: "Prepare for security incidents" },
        { id: "A.5.25", title: "Event assessment", desc: "Evaluate security events" },
        { id: "A.5.26", title: "Incident response", desc: "Respond to security incidents" },
        { id: "A.5.27", title: "Incident learning", desc: "Improve controls from incidents" },
        { id: "A.5.28", title: "Evidence collection", desc: "Preserve evidence for investigations" },
        { id: "A.5.29", title: "Security during disruption", desc: "Protect information during disruptions" },
        { id: "A.5.30", title: "ICT readiness", desc: "Ensure IT supports business continuity" },
        { id: "A.5.31", title: "Legal compliance", desc: "Meet legal and regulatory obligations" },
        { id: "A.5.32", title: "Intellectual property", desc: "Protect IP rights" },
        { id: "A.5.33", title: "Records protection", desc: "Secure business records" },
        { id: "A.5.34", title: "Privacy and PII", desc: "Protect personal data" },
        { id: "A.5.35", title: "Independent review", desc: "Obtain independent security reviews" },
        { id: "A.5.36", title: "Policy compliance", desc: "Ensure adherence to security policies" },
        { id: "A.5.37", title: "Operating procedures", desc: "Document secure operating procedures" }
      ]
    },
    {
      id: "A6",
      title: "A.6 — People Controls",
      icon: Users,
      color: "white",
      controls: [
        { id: "A.6.1", title: "Screening", desc: "Verify background of personnel" },
        { id: "A.6.2", title: "Employment terms", desc: "Define security responsibilities in contracts" },
        { id: "A.6.3", title: "Awareness and training", desc: "Educate users on security" },
        { id: "A.6.4", title: "Disciplinary process", desc: "Address security violations" },
        { id: "A.6.5", title: "Termination/change responsibilities", desc: "Secure access changes" },
        { id: "A.6.6", title: "Confidentiality agreements", desc: "Protect sensitive information" },
        { id: "A.6.7", title: "Remote working", desc: "Secure remote work arrangements" },
        { id: "A.6.8", title: "Event reporting", desc: "Enable staff to report security events" }
      ]
    },
    {
      id: "A7",
      title: "A.7 — Physical Controls",
      icon: Lock,
      color: "blue",
      controls: [
        { id: "A.7.1", title: "Security perimeters", desc: "Protect physical boundaries" },
        { id: "A.7.2", title: "Entry controls", desc: "Restrict physical access" },
        { id: "A.7.3", title: "Securing facilities", desc: "Protect offices and rooms" },
        { id: "A.7.4", title: "Physical monitoring", desc: "Monitor premises" },
        { id: "A.7.5", title: "Environmental protection", desc: "Protect against fire, flood, etc." },
        { id: "A.7.6", title: "Secure areas", desc: "Control work in sensitive areas" },
        { id: "A.7.7", title: "Clear desk/screen", desc: "Reduce exposure of information" },
        { id: "A.7.8", title: "Equipment protection", desc: "Protect equipment placement" },
        { id: "A.7.9", title: "Off-premises assets", desc: "Secure assets outside facilities" },
        { id: "A.7.10", title: "Storage media", desc: "Protect removable media" },
        { id: "A.7.11", title: "Utilities", desc: "Ensure reliable power and utilities" },
        { id: "A.7.12", title: "Cabling", desc: "Protect network and power cabling" },
        { id: "A.7.13", title: "Maintenance", desc: "Maintain equipment securely" },
        { id: "A.7.14", title: "Secure disposal", desc: "Safely dispose or reuse equipment" }
      ]
    },
    {
      id: "A8",
      title: "A.8 — Technological Controls",
      icon: Server,
      color: "maroon",
      controls: [
        { id: "A.8.1", title: "Endpoint security", desc: "Protect laptops and devices" },
        { id: "A.8.2", title: "Privileged access", desc: "Control admin privileges" },
        { id: "A.8.3", title: "Access restriction", desc: "Limit information access" },
        { id: "A.8.4", title: "Source code access", desc: "Protect source code" },
        { id: "A.8.5", title: "Secure authentication", desc: "Use strong authentication" },
        { id: "A.8.6", title: "Capacity management", desc: "Ensure systems can handle demand" },
        { id: "A.8.7", title: "Malware protection", desc: "Prevent and detect malware" },
        { id: "A.8.8", title: "Vulnerability management", desc: "Identify and fix vulnerabilities" },
        { id: "A.8.9", title: "Configuration management", desc: "Secure system configurations" },
        { id: "A.8.10", title: "Information deletion", desc: "Securely delete data" },
        { id: "A.8.11", title: "Data masking", desc: "Hide sensitive data" },
        { id: "A.8.12", title: "Data leakage prevention", desc: "Prevent unauthorized data loss" },
        { id: "A.8.13", title: "Backup", desc: "Back up information" },
        { id: "A.8.14", title: "Redundancy", desc: "Ensure system availability" },
        { id: "A.8.15", title: "Logging", desc: "Record system activities" },
        { id: "A.8.16", title: "Monitoring", desc: "Monitor security events" },
        { id: "A.8.17", title: "Clock synchronization", desc: "Align system clocks" },
        { id: "A.8.18", title: "Privileged utilities", desc: "Control powerful tools" },
        { id: "A.8.19", title: "Software installation", desc: "Restrict software installs" },
        { id: "A.8.20", title: "Network security", desc: "Secure networks" },
        { id: "A.8.21", title: "Network services", desc: "Protect network services" },
        { id: "A.8.22", title: "Network segregation", desc: "Separate networks" },
        { id: "A.8.23", title: "Web filtering", desc: "Control web access" },
        { id: "A.8.24", title: "Cryptography", desc: "Use encryption appropriately" },
        { id: "A.8.25", title: "Secure development lifecycle", desc: "Build security into development" },
        { id: "A.8.26", title: "Application security requirements", desc: "Define security needs" },
        { id: "A.8.27", title: "Secure architecture", desc: "Design systems securely" },
        { id: "A.8.28", title: "Secure coding", desc: "Follow secure coding practices" },
        { id: "A.8.29", title: "Security testing", desc: "Test security before release" },
        { id: "A.8.30", title: "Outsourced development", desc: "Secure third-party development" },
        { id: "A.8.31", title: "Environment separation", desc: "Separate dev/test/prod" },
        { id: "A.8.32", title: "Change management", desc: "Control system changes" },
        { id: "A.8.33", title: "Test information", desc: "Protect test data" },
        { id: "A.8.34", title: "Audit testing protection", desc: "Protect systems during audits" }
      ]
    }
  ];

  const colorClasses = {
    grey: { bg: 'bg-slate-200', text: 'text-slate-700', border: 'border-slate-300' },
    white: { bg: 'bg-white', text: 'text-slate-700', border: 'border-slate-300' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    maroon: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' }
  };

  return (
    <div className="bg-slate-50 py-8 pb-16">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="mb-4 -ml-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="flex items-center gap-3 mb-2">
                <BookOpen className="h-8 w-8 text-slate-900" />
                <h1 className="font-heading font-bold text-3xl text-slate-900">
                  ISO 27001:2022 - Knowledge Base
                </h1>
              </div>
              <p className="text-slate-600">
                Complete reference guide for ISO/IEC 27001:2022 clauses and Annex A controls
              </p>
            </div>
          </div>

          {/* Clauses Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
            <h2 className="font-heading font-semibold text-xl text-slate-900 mb-6 flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-700" />
              ISO/IEC 27001:2022 — Clauses (1–10)
            </h2>
            
            <div className="space-y-3">
              {clauses.map((clause) => (
                <div key={clause.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleClause(clause.id)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-semibold text-slate-700">
                        {clause.id}
                      </span>
                      <span className="font-semibold text-slate-900">{clause.title}</span>
                    </div>
                    {(clause.subclauses.length > 0 || clause.purpose || clause.description) ? (
                      expandedClauses[clause.id] ? 
                        <ChevronDown className="h-5 w-5 text-slate-400" /> : 
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                    ) : null}
                  </button>
                  
                  <AnimatePresence>
                    {expandedClauses[clause.id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-slate-100"
                      >
                        <div className="p-4 bg-slate-50">
                          {clause.description && (
                            <p className="text-slate-600 mb-3">{clause.description}</p>
                          )}
                          {clause.purpose && (
                            <p className="text-slate-600 mb-3">
                              <strong>Purpose:</strong> {clause.purpose}
                            </p>
                          )}
                          {clause.subclauses.length > 0 && (
                            <ul className="space-y-2">
                              {clause.subclauses.map((sub, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-slate-700">
                                  <span className="text-slate-400 mt-1">•</span>
                                  {sub}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>

          {/* Annex A Controls */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
            <h2 className="font-heading font-semibold text-xl text-slate-900 mb-6 flex items-center gap-2">
              <Shield className="h-5 w-5 text-slate-700" />
              Annex A — Information Security Controls (93 Controls)
            </h2>
            
            <div className="space-y-4">
              {annexAControls.map((category) => {
                const colors = colorClasses[category.color];
                const IconComponent = category.icon;
                
                return (
                  <div key={category.id} className={`border ${colors.border} rounded-lg overflow-hidden`}>
                    <button
                      onClick={() => toggleControl(category.id)}
                      className={`w-full flex items-center justify-between p-4 text-left ${colors.bg} hover:opacity-90 transition-opacity`}
                    >
                      <div className="flex items-center gap-3">
                        <IconComponent className={`h-5 w-5 ${colors.text}`} />
                        <div>
                          <span className={`font-semibold ${colors.text}`}>{category.title}</span>
                          <span className={`ml-2 text-sm ${colors.text} opacity-75`}>
                            ({category.controls.length} controls)
                          </span>
                        </div>
                      </div>
                      {expandedControls[category.id] ? 
                        <ChevronDown className={`h-5 w-5 ${colors.text}`} /> : 
                        <ChevronRight className={`h-5 w-5 ${colors.text}`} />
                      }
                    </button>
                    
                    <AnimatePresence>
                      {expandedControls[category.id] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="p-4 bg-white">
                            <div className="grid gap-2">
                              {category.controls.map((control) => (
                                <div 
                                  key={control.id} 
                                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                  <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded whitespace-nowrap">
                                    {control.id}
                                  </span>
                                  <div>
                                    <span className="font-medium text-slate-900">{control.title}</span>
                                    <span className="text-slate-500 mx-2">—</span>
                                    <span className="text-slate-600">{control.desc}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Important Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-2">Important Note</h3>
                <p className="text-amber-700">
                  Please note all the implementation clauses (Clauses 4-10) are <strong>mandatory</strong> to get certified with ISO 27001 Standard. 
                  However, controls selection varies based on business requirements and the operations.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
